import React, { useEffect, useState } from "react";
import { EditIcon, HelpIcon, SaveIcon } from "assets/icons";
import Input from "components/BaseWidgets/form/input";
import Select from "components/BaseWidgets/select";
import Button from "components/BaseWidgets/button";
import { getPhysicalAppearanceDetailsList } from "./helper";
import {
  selectBodyHeight,
  selectBodyWeight,
} from "components/Onboard/components/helper";
import { useSelectOptions } from "hooks/api/useListing";
import { useEditProfile } from "hooks/api/useProfile";
import { displayTextOrDash } from "utils/formatHelper";
import { useQueryClient } from "@tanstack/react-query";
import Tooltip from "components/BaseWidgets/toolTip";

function PhysicalAppearancePanel({
  isEdit,
  id,
  physicalDetails,
  isLock,
  blurredField = [],
}) {
  const [editing, setEditing] = useState(false);
  const queryClient = useQueryClient();

  const physicalData = physicalDetails?.physical_appearance || {};
  const detailsList = getPhysicalAppearanceDetailsList(physicalData);

  // select APIs
  const { bodyType, physicalStatus, complexion, bloodGroup } =
    useSelectOptions();
  const selectedPhysical = {
    "Body Type": bodyType?.options,
    "Physical Status": physicalStatus?.options,
    Complexion: complexion?.options,
    "Blood Group": bloodGroup?.options,
    Weight: selectBodyWeight,
    Height: selectBodyHeight,
  };

  const [formData, setFormData] = useState(() =>
    Object.fromEntries(detailsList.map((item) => [item.label, item.value])),
  );

  useEffect(() => {
    setFormData(
      Object.fromEntries(detailsList.map((item) => [item.label, item.value])),
    );
  }, [JSON.stringify(physicalDetails)]);

  const handleChange = (label, value) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
  };

  const getOptions = (label) => {
    return selectedPhysical[label] || null;
  };

  const editProfileMutation = useEditProfile();

  const normalizeValue = (field) => {
    if (!field) return null;
    if (typeof field === "object" && Object.keys(field).length === 0)
      return null;
    if (Array.isArray(field) && field.length === 0) return null;
    return field?.id || field;
  };

  const preparePhysicalPayload = () => {
    const parseNumber = (value) => {
      if (!value) return null;

      const strValue = String(value).trim();

      const numericPart = parseFloat(strValue.replace(/[^\d.]/g, ""));

      return isNaN(numericPart) ? null : numericPart;
    };

    return {
      weight: parseNumber(formData["Weight"]),
      height: parseNumber(formData["Height"]),
      blood_group: normalizeValue(formData["Blood Group"]),
      complexion: normalizeValue(formData["Complexion"]),
      body_type: normalizeValue(formData["Body Type"]),
      physical_status: normalizeValue(formData["Physical Status"]),
    };
  };

  const handleSave = () => {
    const payload = preparePhysicalPayload();

    editProfileMutation.mutate(payload, {
      onSuccess: () => {
        setEditing(false);
        queryClient.invalidateQueries({
          queryKey: ["my-profile"],
        });
      },
    });
  };

  return (
    <div id={id} className="physical-panel-frame panel-frame">
      <div className="physical-details">
        <div className="header flex items-center justify-between">
          <label className="physical-label font-title-normal">
            Physical appearance
          </label>
          {isEdit && (
            <Button
              variant="custom"
              onClick={() => {
                if (editing) {
                  handleSave();
                } else {
                  setEditing(true);
                }
              }}
              className="edit-button flex items-center gap-1"
            >
              {editing ? (
                <>
                  <SaveIcon /> Save
                </>
              ) : (
                <>
                  <EditIcon /> Edit
                </>
              )}
            </Button>
          )}
        </div>
        <div
          className={`content-grid flex flex-col gap-4 ${isLock && "locked-card"}`}
        >
          {!editing
            ? detailsList.map((item) => {
                const rawValue =
                  typeof formData[item.label] === "object"
                    ? formData[item.label]?.name
                    : formData[item.label];

                const hasValue =
                  rawValue !== null &&
                  rawValue !== undefined &&
                  rawValue !== "";

                const shouldBlur = hasValue && blurredField.includes(item.label);

                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>

                    <div className="flex items-center gap-2">
                      <span
                        className={`item-value ${shouldBlur ? "blur-text" : ""}`}
                      >
                        {displayTextOrDash(rawValue)}
                      </span>

                      {shouldBlur && (
                        <Tooltip title="You should update your details to see this">
                          <span>
                            <HelpIcon />
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                );
            })
            : detailsList.map((item) => {
                const options = getOptions(item.label);
                return (
                  <div
                    key={item.label}
                    className="item-frame flex flex-col gap-1"
                  >
                    <span className="item-label">{item.label}</span>
                    {options ? (
                      <Select
                        SkeletonHeight={24}
                        returnObject={
                          !(item.label === "Height" || item.label === "Weight")
                        }
                        label={""}
                        value={
                          item.label === "Height" || item.label === "Weight"
                            ? options?.find((opt) => {
                                if (!formData[item.label]) return false;
                                const numericValue = parseInt(
                                  formData[item.label],
                                );

                                return opt.value === numericValue;
                              })
                            : formData[item.label]?.id
                              ? options?.find(
                                  (opt) =>
                                    opt.value === formData[item.label]?.id,
                                )
                              : options?.find(
                                  (opt) =>
                                    opt.label?.toLowerCase() ===
                                    String(formData[item.label])?.toLowerCase(),
                                )
                        }
                        options={options}
                        onChange={(val) => handleChange(item.label, val)}
                        inputClassName="item-input-select"
                      />
                    ) : (
                      <Input
                        id={item.label}
                        label={""}
                        value={formData[item.label]}
                        onChange={(val) => handleChange(item.label, val)}
                        inputClassname="item-input"
                      />
                    )}
                  </div>
                );
              })}
        </div>
      </div>
    </div>
  );
}

export default PhysicalAppearancePanel;
